import { useState } from 'react';

import {
  ArrowLeftRight,
  Boxes,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  FileWarning,
  Loader2,
  Receipt,
  RefreshCw,
  SearchCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';

import { Button } from '@/components/shadcn/button';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import paths from '@/lib/paths';
import { toDateString } from '@/lib/utils/date';
import hasItems from '@/lib/utils/hasItems';

import useCheckup from '../../hooks/useCheckup';

import CheckupItem from './CheckupItem';

export default function CheckUp() {
  const { t } = useTranslation();

  const { mode } = useEmailClient();
  const [isOpen, setIsOpen] = useState(mode !== 'Compose');

  const party = useSelectedParty();

  const {
    isLoading,
    hasIssues,
    inventory,
    policies,
    invoices,
    claims,
    transferCandidates,
    switchCandidates,
    lastScanAt,
  } = useCheckup();

  if (!hasIssues) return null;

  return (
    <div className="flex flex-col gap-2 border-b p-2">
      <div className="text-foreground flex h-7 items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          <SearchCheck className="size-3.5" />

          {t('PARTY_CHECKUP_TITLE')}
        </div>

        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen && <ChevronDown className="size-4" />}
          {!isOpen && <ChevronUp className="size-4" />}
        </Button>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-2">
          {!party && (
            <div className="text-muted-foreground text-sm">
              {t('PARTY_CHECKUP_NO_PARTY_SELECTED')}
            </div>
          )}

          {party && isLoading && (
            <div className="text-muted-foreground my-4 flex w-full items-center justify-center gap-2 text-sm">
              <Loader2 className="text-primary-500 size-4 animate-spin" />

              {t('PARTY_CHECKUP_LOADING')}
            </div>
          )}

          {party && !isLoading && (
            <div className="flex flex-col gap-2">
              {hasItems(inventory) && (
                <CheckupItem
                  icon={Boxes}
                  title={t('PARTY_CHECKUP_ALERT_INVENTORY_TITLE')}
                  summary={
                    lastScanAt
                      ? t('PARTY_CHECKUP_ALERT_INVENTORY_SUMMARY_WITH_DATE', {
                          date: toDateString(lastScanAt),
                        })
                      : t('PARTY_CHECKUP_ALERT_INVENTORY_SUMMARY_NO_DATE')
                  }
                />
              )}

              {hasItems(invoices) && (
                <CheckupItem
                  icon={Receipt}
                  to={generatePath(paths.invoices.overview)}
                  title={t('PARTY_CHECKUP_ALERT_INVOICES_TITLE')}
                  summary={t('PARTY_CHECKUP_ALERT_INVOICES_DESC', {
                    count: invoices.length,
                  })}
                />
              )}

              {hasItems(policies) && (
                <CheckupItem
                  icon={CalendarClock}
                  to={generatePath(paths.policies.overview)}
                  title={t('PARTY_CHECKUP_ALERT_POLICIES_TITLE')}
                  summary={t('PARTY_CHECKUP_ALERT_POLICIES_DESC', {
                    count: policies.length,
                  })}
                />
              )}

              {hasItems(claims) && (
                <CheckupItem
                  icon={FileWarning}
                  to={`${generatePath(paths.claims.overview)}?status=OPEN`}
                  title={t('PARTY_CHECKUP_ALERT_CLAIMS_TITLE')}
                  summary={t('PARTY_CHECKUP_ALERT_CLAIMS_DESC', {
                    count: claims.length,
                  })}
                />
              )}

              {hasItems(transferCandidates) && (
                <CheckupItem
                  icon={ArrowLeftRight}
                  to={generatePath(paths.policies.overview)}
                  title={t('PARTY_CHECKUP_ALERT_TRANSFER_TITLE')}
                  summary={t('PARTY_CHECKUP_ALERT_TRANSFER_SUMMARY', {
                    count: transferCandidates.length,
                  })}
                />
              )}

              {hasItems(switchCandidates) && (
                <CheckupItem
                  icon={RefreshCw}
                  to={generatePath(paths.policies.overview)}
                  title={t('PARTY_CHECKUP_ALERT_SWITCH_TITLE')}
                  summary={t('PARTY_CHECKUP_ALERT_SWITCH_SUMMARY', {
                    count: switchCandidates.length,
                  })}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
